import * as React from 'react'
import { get } from '../utils/get'

export enum Sort {
  DEFAULT = 'DEFAULT',
  DESC = 'DESC',
  ASC = 'ASC',
}

const caseSensitiveSorting = {
  [Sort.DESC]:
    <P extends object>(path: string) =>
    (a: P, b: P): number => {
      if (get(a, path) > get(b, path)) return -1
      if (get(a, path) < get(b, path)) return 1
      return 0
    },
  [Sort.ASC]:
    <P extends object>(path: string) =>
    (a: P, b: P): number => {
      if (get(a, path) < get(b, path)) return -1
      if (get(a, path) > get(b, path)) return 1
      return 0
    },
  [Sort.DEFAULT]:
    <P extends object>(path: string) =>
    (a: P, b: P): number =>
      0,
}

const caseInsensitiveSorting = {
  [Sort.DESC]:
    <P extends object>(
      path: string,
      { language, ...options }: { language: string } & Intl.CollatorOptions
    ) =>
    (a: P, b: P): number => {
      return (
        (get(a, path) as string).localeCompare(
          get(b, path),
          language,
          options
        ) * -1
      )
    },
  [Sort.ASC]:
    <P extends object>(
      path: string,
      { language, ...options }: { language: string } & Intl.CollatorOptions
    ) =>
    (a: P, b: P): number => {
      return (get(a, path) as string).localeCompare(
        get(b, path),
        language,
        options
      )
    },
  [Sort.DEFAULT]:
    <P extends object>(
      path: string,
      { language, ...options }: { language: string } & Intl.CollatorOptions
    ) =>
    (a: P, b: P): number =>
      0,
}

export const useSort = <P extends object>(
  startData: P[],
  {
    caseSensitive = true,
    language = 'en',
    ...caseInsensitiveSortOptions
  }: { caseSensitive: boolean; language: string } & Intl.CollatorOptions = {
    caseSensitive: true,
    language: 'en',
  }
) => {
  const [initialData, setInitialData] = React.useState(startData)
  const [data, setData] = React.useState(startData)
  const [options, setOptions] = React.useState({
    sortType: Sort.DEFAULT,
    sortBy: '',
  })

  const getSortedData = (sortType: Sort, path: string) =>
    [...initialData].sort(
      caseSensitive
        ? caseSensitiveSorting[sortType](path)
        : caseInsensitiveSorting[sortType](path, {
            language,
            ...caseInsensitiveSortOptions,
          })
    )

  React.useEffect(() => {
    setData(getSortedData(options.sortType, options.sortBy))
  }, [initialData, options])

  const sort = (sortType: Sort, path: string) =>
    setOptions({ sortType, sortBy: path })

  return {
    ...options,
    data,
    sort,
    updateInitialData: setInitialData,
    initialData,
  }
}
